import { type StateCreator } from 'zustand';
import {
    DefaultPlanIDMapping,
    SwitchYourMatesPlanIDMapping,
    CanstarPlanIDMapping,
    OneBigSwitchPlanIDMapping,
    OlyPlanIDMapping,
    CampaignIDs,
    SwitchYourMatesValidIDs,
    CanstarValidIDs,
    OneBigSwitchValidIDs,
    OlyValidIDs,
    FluxID,
    ProductID,
    CampaignID,
} from '@energy-retailer/constants';

export const getPromoId = (
    planId: ProductID,
    campaignId?: CampaignID
): FluxID => {
    if (!planId) {
        return null;
    }

    const SwitchYourMatesCode =
        SwitchYourMatesPlanIDMapping[planId as SwitchYourMatesValidIDs];
    const CanstarCode = CanstarPlanIDMapping[planId as CanstarValidIDs];
    const OneBigSwitchCode =
        OneBigSwitchPlanIDMapping[planId as OneBigSwitchValidIDs];
    const OlyCode = OlyPlanIDMapping[planId as OlyValidIDs];

    if (campaignId === CampaignIDs.OneBigSwitch && OneBigSwitchCode) {
        return OneBigSwitchCode;
    }

    if (campaignId === CampaignIDs.CanstarBlue && CanstarCode) {
        return CanstarCode;
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- always false
    if (campaignId === CampaignIDs.Oly && OlyCode) {
        return OlyCode;
    }

    if (campaignId === CampaignIDs.SwitchYourMates && SwitchYourMatesCode) {
        return SwitchYourMatesCode;
    }

    return DefaultPlanIDMapping[planId];
};

export type CartSliceState = {
    fluxId: FluxID;
    gasFluxId: FluxID;
    electricityPlanId: string | null;
    electricityPlanSlug: string | null;
    electricityPlanName: string | null;
    gasPlanId: string | null;
    gasPlanSlug: string | null;
    gasPlanName: string | null;
};

export type CartSliceActions = {
    setFluxId: ({
        productId,
        fluxId,
        campaignId,
    }: {
        productId: ProductID;
        fluxId?: string;
        campaignId?: CampaignID;
    }) => void;
    setGasPlan: ({
        planId,
        planSlug,
        planName,
    }: {
        planId: string | null;
        planSlug: string | null;
        planName: string | null;
    }) => void;
    setElectricityPlan: ({
        planId,
        planSlug,
        planName,
    }: {
        planId: string | null;
        planSlug: string | null;
        planName: string | null;
    }) => void;
    clearGasPlan: () => void;
    clearElectricityPlan: () => void;
    setElectricityPlanId: (electricityPlanId: string | null) => void;
    setElectricityPlanSlug: (electricityPlanSlug: string | null) => void;
    setElectricityPlanName: (electricityPlanName: string | null) => void;
    setGasPlanId: (gasPlanId: string | null) => void;
    setGasPlanSlug: (gasPlanSlug: string | null) => void;
    setGasPlanName: (gasPlanName: string | null) => void;
    setGasFluxId: ({
        productId,
        fluxId,
        campaignId,
    }: {
        productId: ProductID;
        fluxId?: string;
        campaignId?: CampaignID;
    }) => void;
    resetCart: () => void;
};

const initialState: CartSliceState = {
    fluxId: null,
    gasFluxId: null,
    electricityPlanId: null,
    electricityPlanSlug: null,
    electricityPlanName: null,
    gasPlanId: null,
    gasPlanSlug: null,
    gasPlanName: null,
};

export const CartSlice: StateCreator<
    CartSliceActions & CartSliceState,
    [['zustand/devtools', never]],
    [],
    CartSliceActions & CartSliceState
> = (set) => ({
    ...initialState,
    setFluxId: ({
        productId,
        fluxId,
        campaignId,
    }: {
        productId: ProductID;
        fluxId?: string;
        campaignId?: CampaignID;
    }) => {
        set(
            { fluxId: fluxId ? (fluxId as FluxID) : getPromoId(productId, campaignId) },
            undefined,
            'CartStore/setFluxId',
        );
    },
    setGasFluxId: ({
        productId,
        fluxId,
        campaignId,
    }: {
        productId: ProductID;
        fluxId?: string;
        campaignId?: CampaignID;
    }) => {
        set(
            { gasFluxId: fluxId ? (fluxId as FluxID) : getPromoId(productId, campaignId) },
            undefined,
            'CartStore/setGasFluxId',
        );
    },
    setGasPlan: ({
        planId,
        planSlug,
        planName,
    }: {
        planId: string | null;
        planSlug: string | null;
        planName: string | null;
    }) => {
        set({ gasPlanId: planId, gasPlanSlug: planSlug, gasPlanName: planName }, undefined, 'CartStore/setGasPlan');
    },
    setElectricityPlan: ({
        planId,
        planSlug,
        planName,
    }: {
        planId: string | null;
        planSlug: string | null;
        planName: string | null;
    }) => {
        set(
            { electricityPlanId: planId, electricityPlanSlug: planSlug, electricityPlanName: planName },
            undefined,
            'CartStore/setElectricityPlan',
        );
    },
    clearGasPlan: () => {
        set({ gasPlanId: null, gasPlanSlug: null, gasPlanName: null }, undefined, 'CartStore/clearGasPlan');
    },
    clearElectricityPlan: () => {
        set(
            { electricityPlanId: null, electricityPlanSlug: null, electricityPlanName: null },
            undefined,
            'CartStore/clearElectricityPlan',
        );
    },
    setElectricityPlanId: (electricityPlanId: string | null) => {
        set({ electricityPlanId }, undefined, 'CartStore/setElectricityPlanId');
    },
    setElectricityPlanSlug: (electricityPlanSlug: string | null) => {
        set({ electricityPlanSlug }, undefined, 'CartStore/setElectricityPlanSlug');
    },
    setElectricityPlanName: (electricityPlanName: string | null) => {
        set({ electricityPlanName }, undefined, 'CartStore/setElectricityPlanName');
    },
    setGasPlanId: (gasPlanId: string | null) => {
        set({ gasPlanId }, undefined, 'CartStore/setGasPlanId');
    },
    setGasPlanSlug: (gasPlanSlug: string | null) => {
        set({ gasPlanSlug }, undefined, 'CartStore/setGasPlanSlug');
    },
    setGasPlanName: (gasPlanName: string | null) => {
        set({ gasPlanName }, undefined, 'CartStore/setGasPlanName');
    },
    resetCart: () => {
        set(initialState, undefined, 'CartStore/reset');
    },
});
